<template>
  <div class="col-4">
    <div class="integration-card">
      <div class="integration-card-header">
        <img :src="$image('assets/' + item.typeInfo.icon)" :alt="item.typeInfo.title"/>

        <div class="header-text">
          <h4 class="integration-card-title">{{ item.typeInfo.title }}</h4>
          <h5 class="integration-card-subtitle">{{ item.typeInfo.subtitle }}</h5>
        </div>
      </div>
      <div class="integration-card-body">
        <div class="content" v-html="item.typeInfo.content"></div>
      </div>
      <div class="integration-card-footer">
        <ButtonElement
            class="btn--ms btn--accent btn--medium btn--md"
            @clickOnButton="showDetailPopup"
        >
          Details
        </ButtonElement>
      </div>
    </div>
  </div>

  <Popup
      v-if="showDetails"
      @close="showDetails = false"
      :show-footer="false"
      :close-with-esc="true"
      width="668px"
      modal-header-class="modal-header-border"
      body-padding="0"
      header-padding="30px 0px 20px 40px"
  >
    <template v-slot:title>
      {{ item.typeInfo.title }}
    </template>
    <template v-slot:body>
      <div class="popup__body">

        <p>Create an <b>API token</b> and Copy & Paste it to the <b>MCP Server</b></p>

        <div class="popup__api">
          <div v-if="!apiToken.token" class="popup__api-token">
            <div class="popup__api-button">
              <ButtonElement class="btn--ms btn--accent btn--medium btn--lg btn--full"
                             :class="{ 'btn--loading': apiTokenLoading }"
                             :disabled="apiTokenLoading"
                             @click="createAPIToken">
                Create API token
              </ButtonElement>
            </div>
          </div>
          <div v-else class="popup__api-token">
            <h5>
              API token<br/>
              <span v-if="apiToken.expireDate">{{ formatDate(apiToken.expireDate) }}</span>
            </h5>
            <div class="popup__api-form">
              <input class="popup__api-form-input" maxlength="255"
                     :value="getApiToken"
                     :autofocus="false"
                     disabled
              />

              <ButtonElement v-if="!showMasked"
                             class="btn--ms btn--neutral-secondary btn--copy btn--md"
                             @click="copyTokenOnly()"
              >
                <LucideIcon icon="Link"/>
                Copy
              </ButtonElement>
            </div>
            <ButtonElement
                class="btn--ms btn--danger btn--xl"
                @click="removeAPIToken()"
                data-tooltip-position="top"
                data-tooltip="Revoke API token"
            >
              <LucideIcon icon="Trash2"/>
            </ButtonElement>
            <ButtonElement
                class="btn--ms btn--accent btn--xl"
                @click="refreshAPIToken()"
                data-tooltip-position="top"
                data-tooltip="Refresh API token"
            >
              <LucideIcon icon="Undo2"/>
            </ButtonElement>
          </div>
        </div>

        <div class="popup__connect">
          <div class="popup__connect-left"></div>
          <div class="popup__connect-right"></div>
        </div>

        <div class="popup--code">
         <pre>
            <code ref="codeRef">
              <span class="line no-copy"><span class="code-comment">// This example demonstrates an MCP server using local connection</span></span>
              <span class="line no-copy"><span class="code-comment no-copy">// MCP-compatible tools automatically run this process</span></span>
              <span class="line no-copy"><span class="code-comment last-comment no-copy">// It uses `npx` to run the local server handler</span></span>
              <span class="line"><span class="code-text">{</span></span>
              <span class="line"><span class="code-accent">  "{{ item.typeInfo.type === 'GITHUBCOPILOT' ? 'servers' : 'mcpServers' }}"</span><span class="code-text">: {</span></span>
              <span class="line"><span class="code-accent">    "metastoryAI"</span><span class="code-text">: {</span></span>
              <span class="line"><span class="code-accent">      "command"</span><span class="code-text">: </span><span class="code-secondary">"npx"</span><span class="code-text">,</span></span>
              <span class="line"><span class="code-accent">      "args"</span><span class="code-text">: [</span><span class="code-secondary">"@metastory/mcp-server"</span><span class="code-text">],</span></span>
              <span class="line"><span class="code-accent">      "env"</span><span class="code-text">: {</span></span>
              <span class="line"><span class="code-accent">        "api_key"</span><span class="code-text">: </span><span class="code-secondary">"{{ getApiToken || '...' }}"</span></span>
              <span class="line"><span class="code-text">      }</span></span>
              <span class="line"><span class="code-text">    }</span></span>
              <span class="line"><span class="code-text">  }</span></span>
              <span class="line"><span class="code-text">}</span></span>
            </code>
          </pre>

          <ButtonElement v-if="!showMasked"
                         class="btn--ms btn--neutral-secondary btn--copy"
                         @click="copyCodeBlock()"
          >
            <LucideIcon icon="Link"/>
            Copy
          </ButtonElement>
        </div>

      </div>
    </template>
  </Popup>

</template>

<script lang="ts">
import {defineComponent, type PropType} from 'vue'
import ButtonElement from '@shared/components/elements/button/button.element.vue'
import {Popup} from "@shared/components";
import {type ApiTokenNs} from "@shared/types";
import {useGeneralStore} from "@shared/stores/general.store";
import {useRouter} from 'vue-router'
import {useApiTokenStore} from "@domains/settings/stores/api-token.store";
import {formatDate, maskLast} from "@shared/helpers";
import {useWorkspaceStore} from "@shared/stores/workspace.store.ts";
import {toast} from '@shared/ms-components'

export default defineComponent({
  name: 'McpCardWidget',
  components: {
    Popup,
    ButtonElement
  },
  setup() {
    const router = useRouter()
    const apiTokenStore = useApiTokenStore()
    const generalStore = useGeneralStore();
    const workspaceStore = useWorkspaceStore();

    return {
      router,
      apiTokenStore,
      generalStore,
      workspaceStore
    }
  },
  props: {
    item: {
      type: Object as PropType<any>,
      required: true
    }
  },
  data() {
    return {
      apiToken: {} as ApiTokenNs.ApiToken,
      showDetails: false,
      apiTokenCreated: false,
      apiTokenLoading: false,
      showMasked: false,
    }
  },
  computed: {
    getApiToken(): string {
      if (this.showMasked) {
        return maskLast(this.apiToken.token, 15)
      } else {
        return this.apiToken.token;
      }
    },
  },
  methods: {
    formatDate,
    showDetailPopup() {
      this.showMasked = false;
      this.apiTokenStore.loadApiToken({type: this.item.typeInfo.type}).then((token) => {
        this.apiToken = token;
        this.showDetails = true
        if (this.apiToken.token) this.showMasked = true;
      })
    },
    copyTokenOnly() {
      if (!this.apiToken.token) return;

      if (!navigator.clipboard) {
        throw new Error("Clipboard API not supported.");
      }

      navigator.clipboard.writeText(this.apiToken.token).then(() => {
        toast.success('API token copied');
      });
    },
    copyCodeBlock() {
      const codeElement = this.$refs.codeRef as HTMLElement | undefined;
      if (!codeElement) return;

      // Clone the content so we can manipulate it without affecting the UI
      const clone = codeElement.cloneNode(true) as HTMLElement;

      // Remove all elements with 'no-copy' class
      clone.querySelectorAll('.no-copy').forEach(el => el.remove());

      // Extract the clean text
      const textToCopy = clone.innerText.trim();

      navigator.clipboard.writeText(textToCopy).then(() => {
        toast.success('Code copied');
      });
    },
    async createAPIToken() {
      this.apiTokenLoading = true;
      this.apiTokenStore.createApiToken({
        type: this.item.typeInfo.type,
        workspaceId: this.workspaceStore.getSelectedWorkspaceId
      })
          .then((token) => {
            this.apiToken = token;
          })
          .finally(() => {
            this.showMasked = false
            this.apiTokenLoading = false
          })
    },
    async refreshAPIToken() {
      this.apiTokenLoading = true;
      this.apiTokenStore.createApiToken({
        type: this.item.typeInfo.type,
        workspaceId: this.workspaceStore.getSelectedWorkspaceId
      })
          .then((token) => {
            this.apiToken = token;
          })
          .finally(() => {
            this.showMasked = false
            this.apiTokenLoading = false
          })
    },
    async removeAPIToken() {
      this.apiTokenLoading = true;
      this.apiTokenStore.removeApiToken({type: this.item.typeInfo.type})
          .then((token) => {
            this.apiToken = token;
          })
          .finally(() => {
            this.showMasked = false;
            this.apiTokenLoading = false
          })
    }
  }
})
</script>

<style lang="scss" scoped>
.integration-card {
  position: relative;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  box-shadow: var(--card-shadow);
  padding: var(--space-16);
  margin-bottom: var(--space-16);
  border-radius: $br-sm;

  &:hover {
    box-shadow: var(--card-shadow-active);
  }

  &-header {
    display: flex;
    align-items: center;

    .header-text {
      margin-left: var(--space-16);

      h4 {
        font-size: var(--fs-14);
        font-weight: 500;
        margin-top: var(--space-8);
        margin-bottom: var(--space-4);
      }

      h5 {
        font-size: var(--fs-12);
        color: var(--text-neutral);
      }
    }

    .tag--integration {
      width: auto;
      height: 25px;
      position: absolute;
      right: var(--space-16);
      top: var(--space-16);
      line-height: 20px;
    }


    button {
      margin-left: auto;
    }
  }

  &-body {
    .content {
      margin-top: var(--space-16);
      margin-bottom: var(--space-16);
      font-size: var(--fs-12);
      color: var(--text-neutral);
    }
  }

  &-footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-8);


    button {
      margin-left: auto;
    }
  }
}

.popup__body {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: var(--space-0) var(--space-40) var(--space-40);
  overflow-y: hidden;
  max-height: 600px;

  h2 {
    font-size: var(--fs-18);
    margin-bottom: var(--space-0);
  }

  h3 {
    font-size: var(--fs-14);
    margin-bottom: var(--space-0);
  }

  h4 {
    margin-bottom: var(--space-0);
  }

  p {
    font-size: var(--fs-14);
    line-height: 22px;
  }

  pre {
    display: flex;
    padding: var(--space-16);
    border-radius: $br-md;
    margin: var(--space-0);
    background: var(--light-300);
    position: relative;
    box-shadow: var(--card-shadow);
    border: 1px solid var(--card-border);
  }

  code {
    position: relative;
    z-index: 1;
    width: max-content;
    min-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }

  .popup__api {
    background: var(--card-bg);
    box-shadow: var(--card-shadow);
    border: 1px solid var(--card-border);
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: var(--space-8);
    padding: var(--space-4);
    border-radius: $br-md;
    position: relative;
    z-index: 2;

    &-button {
      width: 100%;
      display: flex;
      justify-content: center;
      gap: var(--space-4);
    }

    &-token {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: var(--space-8);
      width: 100%;

      &-empty {
        display: flex;
        align-items: center;
        color: var(--text-neutral);
        font-size: var(--fs-12);
        margin-right: var(--space-16);
      }

      h5 {
        white-space: nowrap;
        font-weight: 500;
        margin: var(--space-0);
        padding: var(--space-0) var(--space-8);

        span {
          font-size: var(--fs-12);
          color: var(--text-neutral);
        }
      }
    }

    &-form {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-right: 0;
      border-radius: $br-sm;
      height: 40px;
      padding: var(--space-0) var(--space-4) var(--space-0) var(--space-0);
      width: 100%;
      background-color: var(--form-bg);
      border: 1px solid var(--form-border);
      box-shadow: none !important;

      &-input {
        width: 100%;
        height: 40px !important;
        font-size: var(--fs-14);
        padding: 0 10px;
        margin: var(--space-0);
        border: 0;
        letter-spacing: -0.6px;
        line-height: 20px;
        background-color: transparent;
        z-index: 1;
        position: relative;
        text-overflow: ellipsis;
        color: var(--text-dark);
      }

      &-button {
        display: flex;
        gap: var(--space-4);
      }
    }
  }

  .popup__connect {
    display: flex;
    height: 24px;

    &-left {
      width: 50%;
    }

    &-right {
      border-left: 2px solid var(--border-neutral);
      width: 50%;
    }
  }

  .popup--code {
    position: relative;

    .btn--copy {
      position: absolute;
      right: var(--space-16);
      bottom: var(--space-16);
      z-index: 1;
    }

    .last-comment {
      padding-bottom: var(--space-16);
      display: inline-block;
    }

    .code-comment {
      color: var(--code-comment);
    }

    .code-text {
      color: var(--code-dark);
    }

    .code-accent {
      color: var(--code-accent);
    }

    .code-secondary {
      color: var(--code-secondary);
    }
  }

}


</style>
